import Typography, { type TypographyWeight } from '@ds/components/typography/Typography';
import type { IconSize } from '@ds/icons/icon-types';
import clsx from 'clsx';
import React from 'react';

import styles from './ListItem.module.css';

export interface ListItemProps extends React.HTMLAttributes<HTMLElement> {
    /**
     * The element to render. Defaults to a non-interactive `div`. The containing list
     * (`Listbox.Option`, `Menu.Item`) renders it `as="button"` and supplies the ARIA
     * role — `ListItem` itself is role-agnostic.
     */
    as?: React.ElementType;
    /** The button type when rendered `as="button"`. Defaults to `"button"`, so it never submits a form. */
    type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
    /** Content before the label — an icon, checkbox, or avatar. */
    startAdornment?: React.ReactNode;
    /** Primary label content. */
    children: React.ReactNode;
    /** Overrides the label's default weight (label-1's SemiBold). */
    weight?: TypographyWeight;
    /**
     * Overrides the row's default label colour (`--action-primary` blue) — `Listbox.Option`
     * passes `--text-primary`, since a whole-row blue label suits Menu's action rows but not
     * a value picker's option rows.
     */
    color?: string;
    /** Secondary line rendered under the label. */
    supportingText?: React.ReactNode;
    /** Content after the label — a chevron, status icon, or shortcut. */
    endAdornment?: React.ReactNode;
    /**
     * Size (px) applied to the SVG icon in **both** adornment slots. Overrides the
     * default 20px clamp. Non-SVG slot content (avatar, checkbox, `<img>`) is
     * unaffected. @default 20
     */
    iconSize?: IconSize;
    /** Persistent selected styling (light-blue surface). Visual only — no ARIA here. */
    selected?: boolean;
    /**
     * Disabled styling (faded, non-interactive), plus `aria-disabled` on the row. It never sets the native
     * `disabled` attribute: a disabled menu item or option stays focusable so it is announced as unavailable,
     * and the containing list withholds activation.
     */
    disabled?: boolean;
    /** Destructive styling (red label) — e.g. "Logout", "Delete consignment". */
    danger?: boolean;
    className?: string;
}

/**
 * `ListItem` — the presentational row shared by `Listbox` and `Menu`. It is purely
 * visual: a start adornment, a label, optional supporting text, an end adornment,
 * the Material state-layer (hover/focus tint), and the `selected` / `disabled` /
 * `danger` states. It carries **no** ARIA role and no keyboard behaviour —
 * `Listbox.Option` stamps `role="option"` (that is how `Select`, `Autocomplete`
 * and `PicResultList` reach it, ADR 0052) and `Menu.Item` stamps `role="menuitem"`.
 * Each of those wires its own roving focus and navigation. Nothing in the app
 * renders a `ListItem` directly.
 *
 * Row height and dividers are **not** this component's to vary: `Listbox` owns them
 * through its `density` (56px default / 48px compact) and `divided` props, so the
 * 56px here is the one default and consumers no longer override `min-height` from
 * their own stylesheets. Which defaults design wants is still open with Figma
 * (`docs/tickets/DS-022-listitem-consistency-brief.md`); until it rules, each
 * consumer passes what its own reference shows.
 *
 * Both adornment slots are wrapped by the row itself, so pass content bare
 * (see ADR 0031).
 *
 * @example
 * <ListItem
 *     startAdornment={<UserIcon />}
 *     supportingText="email@mla.com.au"
 *     endAdornment={<ChevronRight />}
 * >
 *     Edit eNVD profile
 * </ListItem>
 */
const ListItem = React.forwardRef<HTMLElement, ListItemProps>(
    (
        {
            as,
            startAdornment,
            children,
            weight,
            color,
            supportingText,
            endAdornment,
            selected,
            disabled,
            danger,
            iconSize,
            className,
            style,
            ...rest
        },
        ref
    ) => {
        const Component = (as ?? 'div') as React.ElementType;

        // Default a real <button> to type="button" so it never submits a form.
        const typeProp = Component === 'button' && !('type' in rest) ? { type: 'button' as const } : {};

        // Drive the adornment icon size and label colour via custom properties, merged
        // over any caller-supplied style so none of these ever clobber each other.
        const mergedStyle = {
            ...(iconSize != null ? { '--list-item-icon-size': `${iconSize}px` } : {}),
            ...(color != null ? { '--list-item-color': color } : {}),
            ...style,
        } as React.CSSProperties;

        return (
            <Component
                ref={ref}
                className={clsx(styles.item, className)}
                style={mergedStyle}
                aria-disabled={disabled || undefined}
                data-selected={selected || undefined}
                data-disabled={disabled || undefined}
                data-danger={danger || undefined}
                {...typeProp}
                {...rest}
            >
                {startAdornment != null && <span className={styles.start}>{startAdornment}</span>}
                <span className={styles.content}>
                    <Typography
                        variant="label-1"
                        weight={weight}
                        component="span"
                    >
                        {children}
                    </Typography>
                    {supportingText != null && (
                        <Typography
                            variant="body-2"
                            component="span"
                            color={disabled ? 'disabled' : 'secondary-2'}
                            className={styles.supporting}
                        >
                            {supportingText}
                        </Typography>
                    )}
                </span>
                {endAdornment != null && <span className={styles.end}>{endAdornment}</span>}
            </Component>
        );
    }
);

ListItem.displayName = 'ListItem';

export { ListItem };
export default ListItem;
